import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { getTicket, updateTicket, upsertTicketView, getTicketByNo } from '../../../api/tickets';
import { canEditTicket } from '../../../middleware/betaPermissions';
import TicketDescription from './TicketDescription';
import TicketComments from './TicketComments';
import TicketAttachments from './TicketAttachments';
import TicketDeliveryLogs from './TicketDeliveryLogs';
import TicketTodoList from './TicketTodoList';
import TicketAssignees from './TicketAssignees';
import TicketReferences from './TicketReferences';
import BetaItemEditModal from './BetaItemEditModal';
import { getTextColorForBg } from '../../../utils/textUtils';
import { useToast } from '../../common/Toast';
import { getAllCompanies } from '../../../api/companies';
import './TicketDetailPage.scss';

const PRIORITY_OPTIONS = [
  { value: 0, label: '無', color: '#94a3b8' },
  { value: 1, label: '低', color: '#94a3b8' },
  { value: 2, label: '中', color: '#f59e0b' },
  { value: 3, label: '高', color: '#ef4444' },
  { value: 4, label: '緊急', color: '#dc2626' },
];

const TicketDetailPage = ({ ticketId, onClose, onNavigate }) => {
  const { profile, viewingCompanyId } = useAuth();
  const toast = useToast();
  const { projects, statusOptions, refreshMyTickets, usersMapByEmail } = useBetaData();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [editingStartDate, setEditingStartDate] = useState(false);
  const [editingEndDate, setEditingEndDate] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [refsRefreshKey, setRefsRefreshKey] = useState(0);
  const commentsRef = useRef(null);
  const todoListRef = useRef(null);

  const handleCopyLink = useCallback(async () => {
    if (!ticket) return;
    const companyId = viewingCompanyId || profile?.company_id;
    let cid = '';
    if (companyId) {
      const { data } = await getAllCompanies();
      const company = (data || []).find((c) => c.id === companyId);
      if (company) cid = company.name.toLowerCase().trim();
    }
    const params = new URLSearchParams();
    if (cid) params.set('cid', cid);
    params.set('ticket', String(ticket.ticket_no));
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('已複製 Ticket 連結');
    } catch {
      toast.error('複製失敗');
    }
  }, [ticket, viewingCompanyId, profile?.company_id, toast]);

  const handleTicketRefClick = useCallback(async (ticketNo) => {
    const cid = viewingCompanyId || profile?.company_id;
    const { data } = await getTicketByNo(ticketNo, cid);
    if (data) {
      onNavigate('ticket', data.project_id, data.id, { ticketNo });
    } else {
      toast.error(`找不到 Ticket #${ticketNo}`);
    }
  }, [viewingCompanyId, profile?.company_id, onNavigate, toast]);

  const loadTicket = useCallback(async (silent = false) => {
    if (!ticketId) return;
    if (!silent) setLoading(true);
    const { data, error } = await getTicket(ticketId);
    if (!error && data) {
      setTicket(data);
      setTitleValue(data.title);
    }
    if (!silent) setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
    // 記錄檢視時間（用於列表未讀標示）
    if (ticketId && profile?.id) {
      upsertTicketView(ticketId, profile.id);
    }
    // 關閉 drawer 時再更新一次，確保查看期間的編輯不會讓自己看到未讀
    return () => {
      if (ticketId && profile?.id) {
        upsertTicketView(ticketId, profile.id);
      }
    };
  }, [loadTicket, ticketId, profile?.id]);

  const canEdit = ticket ? canEditTicket(profile, ticket) : false;

  // 確保 ticket 當前狀態一定出現在 dropdown（處理跨部門可見性）
  const effectiveStatusOptions = useMemo(() => {
    const opts = statusOptions || [];
    if (!ticket) return opts;
    const exists = opts.some((s) => s.value === ticket.status);
    if (exists) return opts;
    return [...opts, { value: ticket.status, label: ticket.status, color: '#94a3b8' }];
  }, [statusOptions, ticket?.status]);

  const handleTitleSave = async () => {
    if (!titleValue.trim() || titleValue === ticket.title) {
      setEditingTitle(false);
      return;
    }
    const { data } = await updateTicket(ticketId, { title: titleValue.trim() });
    if (data) {
      setTicket((prev) => ({ ...prev, title: data.title }));
      refreshMyTickets();
    }
    setEditingTitle(false);
  };

  const handleStatusChange = async (newStatus) => {
    const oldStatus = ticket.status;
    setShowStatusMenu(false);
    const { data } = await updateTicket(ticketId, { status: newStatus });
    if (data) {
      setTicket((prev) => ({
        ...prev,
        status: data.status,
        completed: data.completed,
        completed_at: data.completed_at,
      }));
      refreshMyTickets();
      // Optimistic: 直接 push activity 到 timeline，不 re-fetch
      commentsRef.current?.appendActivity({
        id: `optimistic-status-${Date.now()}`,
        action: 'status_change',
        user_name: profile?.name || profile?.email,
        metadata: { old_status: oldStatus, new_status: newStatus },
        created_at: new Date().toISOString(),
      });
    }
  };

  const handlePriorityChange = async (newPriority) => {
    setShowPriorityMenu(false);
    const { data } = await updateTicket(ticketId, { priority: newPriority });
    if (data) {
      setTicket((prev) => ({ ...prev, priority: data.priority }));
    }
  };

  const handleProjectChange = async (newProjectId) => {
    setShowProjectMenu(false);
    if (newProjectId === ticket.project_id) return;
    const { data } = await updateTicket(ticketId, { project_id: newProjectId });
    if (data) {
      await loadTicket(true);
      refreshMyTickets();
    }
  };

  const handleDateChange = async (field, value) => {
    const { data } = await updateTicket(ticketId, { [field]: value || null });
    if (data) {
      setTicket((prev) => ({ ...prev, [field]: data[field] }));
      refreshMyTickets();
    }
    if (field === 'start_date') setEditingStartDate(false);
    if (field === 'end_date') setEditingEndDate(false);
  };

  if (loading) {
    return (
      <div className="ticket-detail-page">
        <div className="skeleton-loading">
          <div className="skeleton-header">
            <div className="skeleton-line w40 h12" />
            <div className="skeleton-line w20 h12" />
          </div>
          <div className="skeleton-title skeleton-line w80 h24" />
          <div className="skeleton-body-wrap">
            <div className="skeleton-main">
              <div className="skeleton-line w100 h14" />
              <div className="skeleton-line w90 h14" />
              <div className="skeleton-line w60 h14" />
            </div>
            <div className="skeleton-sidebar">
              <div className="skeleton-line w100 h32" />
              <div className="skeleton-line w100 h32" />
              <div className="skeleton-line w70 h14" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-page">
        <div className="error-state">找不到此 Ticket</div>
      </div>
    );
  }

  // 從 effectiveStatusOptions 取得當前狀態資訊
  const currentStatus = effectiveStatusOptions.find((s) => s.value === ticket.status)
    || { value: ticket.status, label: ticket.status, color: '#94a3b8' };
  const currentPriority = PRIORITY_OPTIONS.find((p) => p.value === ticket.priority) || PRIORITY_OPTIONS[0];

  return (
    <div className="ticket-detail-page">
      {/* Header */}
      <div className="ticket-header">
        <div className="header-left">
          {ticket.projects && (
            <div className="project-wrapper">
              <span
                className={`project-tag ${canEdit ? 'editable' : ''}`}
                style={{ backgroundColor: ticket.projects.color || '#667eea', color: getTextColorForBg(ticket.projects.color || '#667eea') }}
                onClick={() => canEdit ? setShowProjectMenu(!showProjectMenu) : (() => { onClose?.(); onNavigate('project', ticket.project_id); })()}
              >
                {ticket.projects.name}
                {canEdit && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke={getTextColorForBg(ticket.projects.color || '#667eea')} strokeWidth="2">
                    <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              {showProjectMenu && (
                <>
                  <div className="menu-overlay" onClick={() => setShowProjectMenu(false)} />
                  <div className="project-dropdown">
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        className={`dropdown-item ${ticket.project_id === p.id ? 'active' : ''}`}
                        onClick={() => handleProjectChange(p.id)}
                      >
                        <span className="project-dot" style={{ backgroundColor: p.color || '#667eea' }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <span className="ticket-id-label copyable" onClick={handleCopyLink} title="點擊複製連結">#{ticket.ticket_no}</span>
          <span className="ticket-meta-inline">
            建立者：{(ticket.creator?.email && usersMapByEmail[ticket.creator.email.toLowerCase()]?.name) || ticket.creator?.email || '未知'}
            {' '}
            建立時間：{new Date(ticket.created_at).toLocaleString('zh-TW', {
              timeZone: 'Asia/Taipei',
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
              hour12: false,
            })}
          </span>
        </div>
        <button className="drawer-close-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Body: 左右雙欄 */}
      <div className="ticket-body">
        {/* 左欄：主內容 */}
        <div className="ticket-main">
          {/* Title */}
          {editingTitle && canEdit ? (
            <input
              className="title-input"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') {
                  setTitleValue(ticket.title);
                  setEditingTitle(false);
                }
              }}
              autoFocus
            />
          ) : (
            <h1
              className={`ticket-title ${canEdit ? 'editable' : ''}`}
              onClick={() => canEdit && setEditingTitle(true)}
            >
              {ticket.title}
            </h1>
          )}

          {/* Description */}
          <TicketDescription
            ticketId={ticketId}
            description={ticket.description}
            canEdit={canEdit}
            onUpdate={(desc) => setTicket((prev) => ({ ...prev, description: desc }))}
            onTicketClick={handleTicketRefClick}
          />

          {/* Attachments */}
          <TicketAttachments ticketId={ticketId} canEdit={canEdit} />

          {/* Comments & Activity Timeline */}
          <TicketComments
            ref={commentsRef}
            ticketId={ticketId}
            refreshKey={commentsRefreshKey}
            onTicketClick={handleTicketRefClick}
            companyId={viewingCompanyId || profile?.company_id}
            onCommentsChange={() => setTimeout(() => setRefsRefreshKey((k) => k + 1), 1000)}
          />
        </div>

        {/* 右欄：Metadata sidebar */}
        <div className="ticket-sidebar">
          {/* Status + Priority (同一 row) */}
          <div className="sidebar-section">
            <div className="status-priority-row">
              <div className="sp-field">
                <div className="section-label">狀態</div>
                <div className="status-wrapper">
                  <button
                    className="status-btn"
                    style={{ backgroundColor: currentStatus.color, color: getTextColorForBg(currentStatus.color) }}
                    onClick={() => canEdit && setShowStatusMenu(!showStatusMenu)}
                    disabled={!canEdit}
                  >
                    {currentStatus.label}
                    {canEdit && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke={getTextColorForBg(currentStatus.color)} strokeWidth="2">
                        <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                  {showStatusMenu && (
                    <>
                      <div className="menu-overlay" onClick={() => setShowStatusMenu(false)} />
                      <div className="status-dropdown">
                        {effectiveStatusOptions.map((status) => (
                          <button
                            key={status.value}
                            className={`dropdown-item ${ticket.status === status.value ? 'active' : ''}`}
                            onClick={() => handleStatusChange(status.value)}
                          >
                            <span className="status-dot" style={{ backgroundColor: status.color }} />
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="sp-field">
                <div className="section-label">優先度</div>
                <div className="priority-wrapper">
                  <button
                    className="priority-btn"
                    style={{ backgroundColor: currentPriority.color, color: getTextColorForBg(currentPriority.color) }}
                    onClick={() => canEdit && setShowPriorityMenu(!showPriorityMenu)}
                    disabled={!canEdit}
                  >
                    {currentPriority.label}
                    {canEdit && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke={getTextColorForBg(currentPriority.color)} strokeWidth="2">
                        <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                  {showPriorityMenu && (
                    <>
                      <div className="menu-overlay" onClick={() => setShowPriorityMenu(false)} />
                      <div className="priority-dropdown">
                        {PRIORITY_OPTIONS.map((p) => (
                          <button
                            key={p.value}
                            className={`dropdown-item ${ticket.priority === p.value ? 'active' : ''}`}
                            onClick={() => handlePriorityChange(p.value)}
                          >
                            <span className="priority-dot" style={{ backgroundColor: p.color }} />
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Assignees */}
          <div className="sidebar-section">
            <TicketAssignees
              ticketId={ticketId}
              assignees={ticket.ticket_assignees || []}
              canEdit={canEdit}
              onUpdate={() => loadTicket(true)}
              onActivityPush={(activity) => commentsRef.current?.appendActivity(activity)}
            />
          </div>

          {/* Dates */}
          <div className="sidebar-section">
            <div className="section-label">日期</div>
            <div className="date-fields">
              <div className="date-field">
                <span className="date-label">開始</span>
                {editingStartDate && canEdit ? (
                  <input
                    type="date"
                    className="date-input"
                    value={ticket.start_date || ''}
                    onChange={(e) => handleDateChange('start_date', e.target.value)}
                    onBlur={() => setEditingStartDate(false)}
                    autoFocus
                  />
                ) : (
                  <span
                    className={`date-value ${canEdit ? 'editable' : ''}`}
                    onClick={() => canEdit && setEditingStartDate(true)}
                  >
                    {ticket.start_date || '未設定'}
                  </span>
                )}
              </div>
              <div className="date-field">
                <span className="date-label">結束</span>
                {editingEndDate && canEdit ? (
                  <input
                    type="date"
                    className="date-input"
                    value={ticket.end_date || ''}
                    onChange={(e) => handleDateChange('end_date', e.target.value)}
                    onBlur={() => setEditingEndDate(false)}
                    autoFocus
                  />
                ) : (
                  <span
                    className={`date-value ${canEdit ? 'editable' : ''}`}
                    onClick={() => canEdit && setEditingEndDate(true)}
                  >
                    {ticket.end_date || '未設定'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Ticket References */}
          <TicketReferences
            ticketId={ticketId}
            ticketNo={ticket.ticket_no}
            companyId={viewingCompanyId || profile?.company_id}
            onTicketClick={handleTicketRefClick}
            refreshKey={refsRefreshKey}
          />

          {/* ToDo List */}
          <div className="sidebar-section todo-section">
            <TicketTodoList
              ref={todoListRef}
              ticketId={ticketId}
              canEdit={canEdit}
              onItemClick={(item) => setEditingItem(item)}
            />
          </div>

          {/* Delivery Logs */}
          <div className="sidebar-section">
            <TicketDeliveryLogs ticketId={ticketId} ticket={ticket} />
          </div>
        </div>
      </div>

      {/* Item Edit Modal */}
      <BetaItemEditModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        item={editingItem}
        onSuccess={() => {
          todoListRef.current?.refresh();
        }}
      />
    </div>
  );
};

export default TicketDetailPage;
